/**
 * Reine Hilfsfunktionen für den X84-Preisrückimport vom Großhandel.
 *
 * Der Großhandel schickt eine X84 mit Einheitspreisen zu denselben
 * Positionsnummern/GAEB-IDs zurück. Diese Funktionen matchen die
 * importierten Einträge gegen die Positionen des geladenen Angebots.
 *
 * Matching-Reihenfolge:
 * 1. GAEB-Original-ID (stabil laut GAEB-Regel 4.6.1), falls auf beiden Seiten vorhanden
 * 2. Normalisierte Positionsnummer ("01.2.005" == "1.2.5")
 */

export interface OfferPositionForMatch {
  id: number;
  positionNumber?: string | null;
  originalId?: string | null;
  gaebOriginalId?: string | null;
  name?: string | null;
}

export interface ImportedPriceEntry {
  gaebOriginalId?: string;
  positionNumber?: string;
  shortText?: string;
  unitPrice?: number;
}

export interface GaebPriceMatch {
  positionId: number;
  unitPrice: number;
  matchedBy: 'id' | 'positionNumber';
  entryText?: string;
}

export interface GaebPriceMatchResult {
  matches: GaebPriceMatch[];
  unmatchedEntries: ImportedPriceEntry[];
  unmatchedPositions: OfferPositionForMatch[];
}

/**
 * Normalisiert eine Positionsnummer für den Vergleich:
 * trimmen, in Segmente splitten, führende Nullen je Segment entfernen.
 * "01.2.005" -> "1.2.5"
 */
export function normalizePositionNumber(positionNumber: string | null | undefined): string {
  if (positionNumber === null || positionNumber === undefined) return '';
  return String(positionNumber)
    .trim()
    .split('.')
    .map((segment) => {
      const trimmed = segment.trim();
      // Führende Nullen entfernen, aber mindestens eine Ziffer behalten ("000" -> "0")
      const withoutLeadingZeros = trimmed.replace(/^0+(?=\d)/, '');
      return withoutLeadingZeros;
    })
    .filter((segment) => segment.length > 0)
    .join('.');
}

/**
 * Vergleicht zwei Positionsnummern segmentweise (numerisch, sonst lexikografisch).
 * Rückgabe: <0 wenn a < b, 0 wenn gleich, >0 wenn a > b.
 * Kürzere Nummern, die Präfix der längeren sind, gelten als kleiner ("1.2" < "1.2.1").
 */
export function comparePositionNumbers(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  const segmentsA = normalizePositionNumber(a).split('.').filter((s) => s.length > 0);
  const segmentsB = normalizePositionNumber(b).split('.').filter((s) => s.length > 0);

  const len = Math.min(segmentsA.length, segmentsB.length);
  for (let i = 0; i < len; i++) {
    const segA = segmentsA[i];
    const segB = segmentsB[i];
    const numA = /^\d+$/.test(segA) ? parseInt(segA, 10) : Number.NaN;
    const numB = /^\d+$/.test(segB) ? parseInt(segB, 10) : Number.NaN;

    let diff: number;
    if (Number.isFinite(numA) && Number.isFinite(numB)) {
      diff = numA - numB;
    } else {
      diff = segA.localeCompare(segB, 'de');
    }
    if (diff !== 0) return diff < 0 ? -1 : 1;
  }
  if (segmentsA.length === segmentsB.length) return 0;
  return segmentsA.length < segmentsB.length ? -1 : 1;
}

/**
 * True, wenn `prefix` ein Positionsnummern-Präfix von `positionNumber` ist
 * (segmentweise, normalisiert). "1.2" ist Präfix von "01.02.005".
 */
export function isPositionNumberPrefix(
  prefix: string | null | undefined,
  positionNumber: string | null | undefined,
): boolean {
  const prefixSegments = normalizePositionNumber(prefix).split('.').filter((s) => s.length > 0);
  const posSegments = normalizePositionNumber(positionNumber).split('.').filter((s) => s.length > 0);
  if (prefixSegments.length === 0 || prefixSegments.length > posSegments.length) return false;
  return prefixSegments.every((segment, index) => segment === posSegments[index]);
}

/**
 * Matcht importierte GAEB-Einträge (z.B. aus einer X84 des Großhandels)
 * gegen die Positionen des geladenen Angebots.
 *
 * Nur Einträge mit unitPrice > 0 werden gematcht; alle übrigen landen
 * in `unmatchedEntries`.
 */
export function matchGaebPrices(
  offerPositions: OfferPositionForMatch[],
  importedEntries: ImportedPriceEntry[],
): GaebPriceMatchResult {
  const matches: GaebPriceMatch[] = [];
  const unmatchedPositions: OfferPositionForMatch[] = [];
  const matchedEntries = new Set<ImportedPriceEntry>();

  // Nur Einträge mit positivem Einheitspreis sind Match-Kandidaten
  const candidates = importedEntries.filter(
    (entry) => typeof entry.unitPrice === 'number' && Number.isFinite(entry.unitPrice) && entry.unitPrice > 0,
  );

  const byId = new Map<string, ImportedPriceEntry>();
  const byPositionNumber = new Map<string, ImportedPriceEntry>();
  for (const entry of candidates) {
    const id = entry.gaebOriginalId?.trim();
    if (id && !byId.has(id)) byId.set(id, entry);
    const normalized = normalizePositionNumber(entry.positionNumber);
    if (normalized && !byPositionNumber.has(normalized)) byPositionNumber.set(normalized, entry);
  }

  for (const position of offerPositions) {
    let matchedEntry: ImportedPriceEntry | undefined;
    let matchedBy: 'id' | 'positionNumber' | undefined;

    // 1. Primär: GAEB-Original-ID (falls auf beiden Seiten vorhanden)
    const positionGaebId = position.gaebOriginalId?.trim();
    if (positionGaebId && byId.has(positionGaebId)) {
      matchedEntry = byId.get(positionGaebId);
      matchedBy = 'id';
    }

    // 2. Sekundär: normalisierte Positionsnummer
    if (!matchedEntry) {
      const normalized = normalizePositionNumber(position.positionNumber);
      if (normalized && byPositionNumber.has(normalized)) {
        matchedEntry = byPositionNumber.get(normalized);
        matchedBy = 'positionNumber';
      }
    }

    if (matchedEntry && matchedBy) {
      matchedEntries.add(matchedEntry);
      matches.push({
        positionId: position.id,
        unitPrice: matchedEntry.unitPrice as number,
        matchedBy,
        entryText: matchedEntry.shortText,
      });
    } else {
      unmatchedPositions.push(position);
    }
  }

  const unmatchedEntries = importedEntries.filter((entry) => !matchedEntries.has(entry));

  return { matches, unmatchedEntries, unmatchedPositions };
}
